import { useRef, useState } from "react";
import {
  Download,
  Upload,
  ShieldCheck,
  ArrowDownToLine,
  Check,
  ExternalLink,
} from "lucide-react";
import { useCity } from "../state/CityContext";
import { downloadRun } from "../lib/persistence";
import { validateRun } from "../simulation/engine";
import { geography } from "../lib/geography";
import { Dialog } from "./Primitives";
export function HelpDialog({
  onClose,
  offlineReady,
  canInstall,
  installApp,
}: {
  onClose: () => void;
  offlineReady: boolean;
  canInstall: boolean;
  installApp: () => Promise<boolean>;
}) {
  const { state, dispatch, saveStatus } = useCity();
  const [tab, setTab] = useState<"about" | "model" | "device">("about"),
    [message, setMessage] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const importFile = async (f: File) => {
    if (f.size > 1_000_000) {
      setMessage(
        "This file is too large. Choose a PULSE scenario JSON file under 1 MB.",
      );
      return;
    }
    try {
      const data = JSON.parse(await f.text());
      const run =
        data.format === "pulse-run" && data.version === 1
          ? validateRun(data.run)
          : null;
      const comparison = data.comparison ? validateRun(data.comparison) : null;
      if (!run || (data.comparison && !comparison)) throw Error();
      dispatch({ type: "restore", run, comparison });
      setMessage("Experiment restored. Playback is paused.");
    } catch {
      setMessage(
        "This is not a valid PULSE experiment. The current scenario has been kept.",
      );
    }
  };
  return (
    <Dialog title="A living city. An open experiment." onClose={onClose}>
      <div className="help-tabs" aria-label="Field notes sections">
        {(["about", "model", "device"] as const).map((t) => (
          <button
            key={t}
            aria-pressed={tab === t}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
          >
            {t === "about"
              ? "The atlas"
              : t === "model"
                ? "The model"
                : "Your device"}
          </button>
        ))}
      </div>
      {tab === "about" ? (
        <div className="help-content">
          <p>
            PULSE lets you explore how a city’s systems influence one another.
            It is an experimental digital twin-inspired atlas centred on
            Lichfield, Staffordshire.
          </p>
          <h3>What is real?</h3>
          <p>
            Street lines, building footprints, water bodies, parks and rail
            corridors are drawn from a bundled OpenStreetMap extract downloaded
            on {geography.extracted}. Landmarks are geographically anchored.
            Some mapped areas, including Beacon Park, are approximate in the
            source.
          </p>
          <h3>What is imagined?</h3>
          <p>
            All infrastructure links, building heights, service capacities,
            district boundaries, exposure weights, household totals, incidents
            and activity. Spires are an illustrative interpretation. Nothing
            here is a live service feed.
          </p>
          <a
            className="secondary-button"
            href={`${import.meta.env.BASE_URL}geography/lichfield.geojson`}
            download
          >
            <Download size={15} />
            Download geographical extract
          </a>
          <a
            className="text-button"
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer"
          >
            © OpenStreetMap contributors · ODbL 1.0 <ExternalLink size={13} />
          </a>
          <h3>Explore the connections</h3>
          <p>
            Drag the atlas to pan. Scroll or pinch to zoom. Select a place on
            the map or in the place list. Use the map controls to switch between
            plan and 2.5D views. Every control has a keyboard alternative.
          </p>
        </div>
      ) : tab === "model" ? (
        <div className="help-content">
          <h3>A deterministic stress model</h3>
          <p>
            The same parameters and decisions always produce the same result.
            The model advances in five-minute steps over a two-hour experiment.
            Forcing rises to its peak at minute 45, stays until minute 75, then
            tapers.
          </p>
          <ol className="model-chain">
            <li>
              <strong>Water</strong>
              <span>Rainfall × area exposure, with a smaller wind term.</span>
            </li>
            <li>
              <strong>Energy</strong>
              <span>Demand above 105% and wind, plus 0.29 × water stress.</span>
            </li>
            <li>
              <strong>Mobility</strong>
              <span>
                Rail disruption × exposure, plus 0.46 × water and 0.30 × energy
                stress.
              </span>
            </li>
          </ol>
          <p>
            Each new stress value blends 52% target pressure with 48% previous
            pressure. Scores remain between 0 and 100. This is a teaching model,
            not a hydraulic, traffic or electrical solver.
          </p>
          <h3>Response constraints</h3>
          <p>
            Eight crews share one resource pool. Each area can receive up to
            three per system. Crews arrive after five minutes, then reach full
            effect over twenty minutes. One fully effective crew subtracts 23
            water, 24 energy or 25 mobility target stress points. Effects also
            propagate into dependent systems.
          </p>
          <h3>Measuring an outcome</h3>
          <p>
            City continuity averages 100 minus each area’s combined stress,
            weighted by invented household counts. Combined stress is 33% water,
            40% energy and 27% mobility. Affected households are area households
            × combined stress × 0.68. Household-hours integrate that count
            through time.
          </p>
          <p className="small-note">
            Model coefficients are invented and uncalibrated. No emergency
            planning, operational or safety conclusions should be drawn.
          </p>
        </div>
      ) : (
        <div className="help-content">
          <div className="device-status">
            <ShieldCheck size={22} />
            <div>
              <h3>
                {offlineReady
                  ? "Ready for offline exploration"
                  : "Offline cache is not ready here"}
              </h3>
              <p>
                {offlineReady
                  ? "The application, fonts and geographical data are cached on this device."
                  : "Offline caching requires the production build served over HTTPS or localhost. Reload after its first successful load."}
              </p>
            </div>
          </div>
          <h3>Your experiment stays here</h3>
          <p>
            {saveStatus === "saved"
              ? "The experiment is saved on this device in IndexedDB, with a local storage fallback."
              : "Saving is unavailable or still pending. Export a copy to preserve your experiment."}{" "}
            Playback always resumes paused. Browser storage can be cleared or
            evicted.
          </p>
          <div className="dialog-actions">
            <button
              className="secondary-button"
              onClick={() => downloadRun(state.run, state.comparison)}
            >
              <Download size={15} />
              Export experiment
            </button>
            <button
              className="secondary-button"
              onClick={() => file.current?.click()}
            >
              <Upload size={15} />
              Import experiment
            </button>
          </div>
          <input
            className="sr-only"
            ref={file}
            type="file"
            accept=".json,application/json"
            aria-label="Import PULSE experiment file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importFile(f);
              e.target.value = "";
            }}
          />
          {message && (
            <p role="status" className="import-message">
              <Check size={15} />
              {message}
            </p>
          )}
          <h3>Keep PULSE on your home screen</h3>
          <p>
            {canInstall
              ? "Your browser supports installing PULSE. Use the button below."
              : "On iPhone or iPad, open the hosted application in Safari, use Share, then Add to Home Screen. On supported desktop and Android browsers, use the browser’s install menu. Installation requires HTTPS or localhost."}
          </p>
          {canInstall && (
            <button
              className="primary-button"
              onClick={() => void installApp()}
            >
              <ArrowDownToLine size={16} />
              Install PULSE
            </button>
          )}
          <p className="small-note">
            No account. No analytics. No remote data requests during normal use.
            Real-time feeds and collaboration are future integration points.
          </p>
        </div>
      )}
    </Dialog>
  );
}
